import React, { useEffect, useMemo } from 'react';
import { List } from 'immutable';
import config from 'react-global-configuration';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CoreLink } from '@uncinc/react-drupal-core';
import type {
  ImmutableEntity,
  ImmutableRootState,
  Langcodes,
  OptionalArrayField,
  ReferenceField,
  TaxonomyEntity,
  TextPlainField,
} from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { DOMAIN_KVNL } from '../../config/constants';
import { getVocabulary } from '../../store/actions/UI';
import PrevNextCard from './PrevNextCard';

import './style.scss';

// Placeholder card that adds remaining space, or shows introduction page when on the first page.
const CardPlaceholder = () => (
  <div className="PrevNext__cardPlaceholder" />
);

interface Term extends TaxonomyEntity {
  field_sub_title: OptionalArrayField<TextPlainField[]>;
  field_milestone_label: OptionalArrayField<TextPlainField[]>;
  field_campaign?: OptionalArrayField<ReferenceField<TaxonomyEntity>[]>;
}

interface Term extends ImmutableEntity<Term> {}

interface PrevNextProps {
  originTid: number;
  parentUrl: string;
  parentUrlTitle: string;
  title?: string;
  vocabulary: string;
  filterFunction?: (term: Term) => boolean;
}

const usePrevNextTerms = (
  vocabulary: string,
  tid: number,
  filterFunction?: (term: Term) => boolean,
) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getVocabulary(vocabulary));
  }, [vocabulary, dispatch]);

  const entityTerms = useSelector<ImmutableRootState<List<Term>>>(
    (state) => state.getIn(['uiInternal', 'vocabularies', vocabulary], List()),
  ) as List<Term>;

  return useMemo(() => {
    const filteredTerms = filterFunction ? entityTerms.filter(filterFunction) : entityTerms;
    const currentIndex = filteredTerms.findIndex(
      (termIndex) => termIndex.getIn(['tid', 0, 'value']) === tid,
    );
    const prev = currentIndex > 0 && filteredTerms.get(currentIndex - 1);
    const next = currentIndex < filteredTerms.size && filteredTerms.get(currentIndex + 1);

    return { prev, next, currentIndex };
  }, [entityTerms, filterFunction, tid]);
};

const PrevNext: React.FC<PrevNextProps> = ({
  originTid,
  parentUrl,
  parentUrlTitle,
  title = '',
  vocabulary,
  filterFunction,
}) => {
  const { t } = useTranslation();
  const language = config.get('DEFAULT_LANGUAGE') as Langcodes;

  const { prev, next, currentIndex } = usePrevNextTerms(vocabulary, originTid, filterFunction);

  return (
    <div className="PrevNext">
      <Container>
        <div className="row">
          <div className="col-12 col-lg-8 offset-lg-2">
            <div className="PrevNext__wrapper">
              <div className="PrevNext__titleBox">
                <h3 className="PrevNext__title">{title}</h3>
                {parentUrl && (
                  <CoreLink href={parentUrl} className="PrevNext__link">
                    {parentUrlTitle} <div className="icon icon-chevron-right" />
                  </CoreLink>
                )}
              </div>
              <div className="PrevNext__cardBox">
                {prev ? (
                  <>
                    {config.get('CURRENT_DOMAIN') === DOMAIN_KVNL && (
                      <div className="PrevNext__svgLeft" />
                    )}

                    <PrevNextCard
                      title={prev.getIn(['name', 0, 'value'], '')}
                      body={prev.getIn(['field_sub_title', 0, 'value'], '')}
                      label={prev.getIn(
                        ['field_milestone_label', 0, 'value'],
                        `${t('general.step')} ${currentIndex}`,
                      )}
                      // @ts-ignore
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                      path={prev.getIn(['paths', language], prev.get('paths')?.first())}
                      icon={prev.getIn(['name', 0, 'value'], '').toLowerCase().replace(/ /g, '')}
                      left
                    />
                  </>
                ) : (
                  <CardPlaceholder />
                )}

                {next ? (
                  <>
                    {config.get('CURRENT_DOMAIN') === DOMAIN_KVNL && (
                      <div className="PrevNext__svgRight" />
                    )}

                    <PrevNextCard
                      title={next.getIn(['name', 0, 'value'], '')}
                      body={next.getIn(['field_sub_title', 0, 'value'], '')}
                      label={next.getIn(
                        ['field_milestone_label', 0, 'value'],
                        `Stap ${currentIndex + 2}`,
                      )}
                      // @ts-ignore
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                      path={next.getIn(['paths', language], next.get('paths').first())}
                      icon={next.getIn(['name', 0, 'value'], '').toLowerCase().replace(/ /g, '')}
                    />
                  </>
                ) : (
                  <CardPlaceholder />
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default PrevNext;
